import { useCallback, useEffect, useState } from 'react';
import { type Outcome, approve, discard, listApprovals, postpone } from '../api';
import type { EditedText, PendingMessage } from '../api-types';

export type ApprovalsState =
  | { status: 'loading' }
  | { status: 'error'; httpStatus: number }
  | {
      status: 'ready';
      items: PendingMessage[];
      nextCursor: string | null;
      loadingMore: boolean;
      moreFailed: boolean;
    };

export type ApprovalAction =
  { kind: 'approve'; edited?: EditedText } | { kind: 'postpone' } | { kind: 'discard' };

function run(id: string, action: ApprovalAction): Promise<Outcome<unknown>> {
  switch (action.kind) {
    case 'approve':
      return approve(id, action.edited);
    case 'postpone':
      return postpone(id);
    case 'discard':
      return discard(id);
  }
}

/** The approvals queue, and the four decisions on one item: the item leaves the list once decided. */
export function useApprovals() {
  const [state, setState] = useState<ApprovalsState>({ status: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void listApprovals().then((answer) => {
      if (!live) return;
      setState(
        answer.ok
          ? {
              status: 'ready',
              items: answer.value.items,
              nextCursor: answer.value.nextCursor,
              loadingMore: false,
              moreFailed: false,
            }
          : { status: 'error', httpStatus: answer.status },
      );
    });
    return () => {
      live = false;
    };
  }, [attempt]);

  const decide = useCallback(async (id: string, action: ApprovalAction): Promise<boolean> => {
    setBusyId(id);
    setFailed(false);
    const answer = await run(id, action);
    setBusyId(null);
    if (!answer.ok) {
      setFailed(true);
      return false;
    }
    setState((current) =>
      current.status === 'ready'
        ? { ...current, items: current.items.filter((item) => item.message.id !== id) }
        : current,
    );
    return true;
  }, []);

  const loadMore = useCallback(() => {
    if (state.status !== 'ready' || state.nextCursor === null || state.loadingMore) return;
    setState({ ...state, loadingMore: true, moreFailed: false });
    void listApprovals(state.nextCursor).then((answer) => {
      setState((current) => {
        if (current.status !== 'ready') return current;
        if (!answer.ok) return { ...current, loadingMore: false, moreFailed: true };
        const known = new Set(current.items.map((item) => item.message.id));
        const fresh = answer.value.items.filter((item) => !known.has(item.message.id));
        return {
          ...current,
          items: [...current.items, ...fresh],
          nextCursor: answer.value.nextCursor,
          loadingMore: false,
        };
      });
    });
  }, [state]);

  return { state, busyId, failed, decide, loadMore, reload: () => setAttempt((n) => n + 1) };
}
